import React, { useContext, useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PersonIcon from '@mui/icons-material/Person';
import whatsappIcon from '../../../../../assets/Whatsapp.svg';
import styles from '../../../../../assets/styles/componentStyles/voiceAgentStyles/voiceAgent.module.scss';
import globalStyles from '../../../../../assets/styles/global.module.scss';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';
import { useVoiceCallStore } from '../../../../../store/voiceCallStore';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import agent from '../../../../../services/api';
import { getDefaultPeerConnectionConfig, waitForIceGatheringComplete } from '../../../../../utils/webrtcIceGathering';
import GlobalButton from '../../../../../components/utility/globalButton/button';
import { useGenericStore } from '../../../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import {
    getBusinessPhoneLabel,
    getEndUserDisplayName,
    getEndUserPhoneNumber,
    resolveInboundCallIds
} from '../../../../../utils/voiceCallDetails'
import { formatPhoneNumber } from '../../../utils';

type MicPermission = 'idle' | 'granted' | 'denied';

const VoiceAIInboundPopup = () => {
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const getWabaNameAndPhoneNumberBytIntegratedChannelId = useRuleBasedInboxStore((state) => state.getWabaNameAndPhoneNumberBytIntegratedChannelId);


    const { callingWabaDetails, callingPersonDetails, inboundCallExecutionData, voiceCallDetails, flagAutoAcceptInboundCall, setFlagAutoAcceptInboundCall, isCallConnected, setIsCallConnected, isCalling, setIsCalling, callDuration, voiceCallId,
        setVoiceCallId, startCallTimer, stopCallTimer, clearInboundCallState } = useVoiceCallStore(useShallow((state) => ({
        callingWabaDetails: state.callingWabaDetails,
        callingPersonDetails: state.callingPersonDetails,
        inboundCallExecutionData: state.inboundCallExecutionData,
        voiceCallDetails: state.voiceCallDetails,
        flagAutoAcceptInboundCall: state.flagAutoAcceptInboundCall,
        setFlagAutoAcceptInboundCall: state.setFlagAutoAcceptInboundCall,
        isCallConnected: state.isCallConnected,
        setIsCallConnected: state.setIsCallConnected,
        isCalling: state.isCalling,
        setIsCalling: state.setIsCalling,
        callDuration: state.callDuration,
        voiceCallId: state.voiceCallId,
        setVoiceCallId: state.setVoiceCallId, 
        startCallTimer: state.startCallTimer, 
        stopCallTimer: state.stopCallTimer,
        clearInboundCallState: state.clearInboundCallState
    })));


    const { showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    const [micPermission, setMicPermission] = useState<MicPermission>('idle');
    const [isRequestingMic, setIsRequestingMic] = useState(false);
    const [isCheckingMicPermission, setIsCheckingMicPermission] = useState(true);
    const [isAcceptingCall, setIsAcceptingCall] = useState(false);
    const [isRejectingCall, setIsRejectingCall] = useState(false);
    const [isEndingCall, setIsEndingCall] = useState(false);
    const [isMicMuted, setIsMicMuted] = useState(false);

    const localStreamRef = useRef<MediaStream | null>(null);
    const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
    const remoteAudioElementRef = useRef<HTMLAudioElement | null>(null);
    const isCallConnectedRef = useRef<boolean>(false);
    const isMicMutedRef = useRef<boolean>(false);

    const findPersonDetail = (field: string) => {
        const personMetaData = callingPersonDetails?.thread_metadata;
        const fieldLower = field.toLowerCase();
        switch (fieldLower) {
            case 'phone':
                return personMetaData?.find((data) => data?.metadata_type_name === 'phone_number')?.metadata_value;
            case 'name':
                return personMetaData?.find((data) => data?.metadata_type_name === 'name')?.metadata_value;
            default:
                return personMetaData?.find((data) => data?.metadata_type_name === 'name')?.metadata_value;
        }
    };

    const getCallerName = () => {
        return getEndUserDisplayName(voiceCallDetails, findPersonDetail('name'));
    };

    const getCallerPhone = () => {
        return getEndUserPhoneNumber(voiceCallDetails, findPersonDetail('phone'));
    };

    const getCallerDisplayName = () => getCallerName();

    const getReceivingOnLabel = () => {
        return getBusinessPhoneLabel(voiceCallDetails, callingWabaDetails, 'inbound');
    };

    const shouldShowPersonIcon = () => {
        const name = voiceCallDetails?.end_user?.name || findPersonDetail('name');
        if (!name || name.toUpperCase() === 'UNKNOWN') {
            return true;
        }
        return false;
    };

    const getCallerInitial = () => {
        if (shouldShowPersonIcon()) {
            return null;
        }
        const displayName = getCallerDisplayName();
        const firstChar = displayName.trim().charAt(0);
        return firstChar ? firstChar.toUpperCase() : '?';
    };

    const renderPulsingCallerAvatar = () => (
        <div className={styles.voiceCallAvatarPulseWrap}>
            <span className={`${styles.voiceCallAvatarPulseRing} ${styles.voiceCallAvatarPulseRingOne}`} aria-hidden />
            <span className={`${styles.voiceCallAvatarPulseRing} ${styles.voiceCallAvatarPulseRingTwo}`} aria-hidden />
            <div className={styles.voiceCallAvatarPulseCore}>
                {shouldShowPersonIcon() ? (
                    <PersonIcon sx={{ color: '#fff', fontSize: 34 }} />
                ) : (
                    getCallerInitial()
                )}
            </div>
        </div>
    );

    const formatCallDuration = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = (seconds % 60).toString().padStart(2, '0');
        return `${minutes}:${secs}`;
    };

    const acquireMicrophoneAccess = async (showErrorOnFailure = true): Promise<boolean> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 48000,
                    channelCount: 1,
                },
            });

            localStreamRef.current = stream;
            setMicPermission('granted');
            return true;
        } catch (err: any) {
            console.error(`Microphone access denied: ${err.message}`);
            setMicPermission('denied');
            if (showErrorOnFailure) {
                console.warn('Microphone access is required to accept voice calls');
            }
            return false;
        }
    };

    const checkInitialMicrophonePermission = async () => {
        try {
            if (navigator.permissions) {
                const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });

                if (permissionStatus.state === 'granted') {
                    await acquireMicrophoneAccess(false);
                    return;
                }
                if (permissionStatus.state === 'denied') {
                    setMicPermission('denied');
                    return;
                }

                permissionStatus.onchange = () => {
                    if (permissionStatus.state === 'granted') {
                        acquireMicrophoneAccess(false);
                    } else if (permissionStatus.state === 'denied') {
                        setMicPermission('denied');
                    } else {
                        setMicPermission('idle');
                    }
                };
                return;
            }
        } catch {
            console.log('Permission API not fully supported, falling back to device enumeration');
        }

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasMicPermission = devices.some((device) => device.kind === 'audioinput' && device.label !== '');
            if (hasMicPermission) {
                await acquireMicrophoneAccess(false);
            }
        } catch {
            console.log('Could not determine microphone permission status');
        }
    };

    const requestMicrophone = async () => {
        try {
            setIsRequestingMic(true);
            await acquireMicrophoneAccess();
        } finally {
            setIsRequestingMic(false);
        }
    };

    const cleanupLocalStream = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((track) => track.stop());
            localStreamRef.current = null;
        }
        isMicMutedRef.current = false;
        setIsMicMuted(false);
    };

    const applyMicMuteState = (muted: boolean) => {
        const enabled = !muted;

        localStreamRef.current?.getAudioTracks().forEach((track) => {
            track.enabled = enabled;
        });

        peerConnectionRef.current?.getSenders().forEach((sender) => {
            if (sender.track?.kind === 'audio') {
                sender.track.enabled = enabled;
            }
        });
    };

    const toggleMicMute = () => {
        const nextMuted = !isMicMutedRef.current;
        isMicMutedRef.current = nextMuted;
        applyMicMuteState(nextMuted);
        setIsMicMuted(nextMuted);
    };

    const cleanupWebRTC = () => {
        stopCallTimer();
        isCallConnectedRef.current = false;
        setIsCallConnected(false);
        setIsCalling(false);

        if (remoteAudioElementRef.current) {
            try {
                remoteAudioElementRef.current.pause();
                remoteAudioElementRef.current.srcObject = null;
                remoteAudioElementRef.current.remove();
                remoteAudioElementRef.current = null;
            } catch (err) {
                console.error('Error cleaning up audio element:', err);
            }
        }

        if (peerConnectionRef.current) {
            peerConnectionRef.current.close();
            peerConnectionRef.current = null;
        }

        cleanupLocalStream();
    };

    const handleRemoteAudioTrack = (event: RTCTrackEvent) => {
        if (!remoteAudioElementRef.current) {
            const audioElement = document.createElement('audio');
            audioElement.id = 'inbound-remote-audio-player';
            audioElement.autoplay = true;
            audioElement.volume = 1.0;
            audioElement.muted = false;
            audioElement.setAttribute('playsinline', '');
            audioElement.style.position = 'fixed';
            audioElement.style.opacity = '0';
            audioElement.style.pointerEvents = 'none';
            document.body.appendChild(audioElement);
            remoteAudioElementRef.current = audioElement;
        }

        const audioStream = event.streams?.[0] ?? (event.track ? new MediaStream([event.track]) : null);
        if (!audioStream || !remoteAudioElementRef.current) return;

        remoteAudioElementRef.current.srcObject = audioStream;
        remoteAudioElementRef.current.play().catch((err) => {
            console.warn('Inbound remote audio play blocked:', err);
        });
    };

    const createPeerConnection = async (): Promise<RTCPeerConnection> => {
        const peerConnection = new RTCPeerConnection(getDefaultPeerConnectionConfig());

        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach((track) => {
                track.enabled = !isMicMutedRef.current;
                if (track.readyState === 'live') {
                    peerConnection.addTrack(track, localStreamRef.current!);
                }
            });
        }

        peerConnection.onicecandidateerror = (event) => {
            console.error(`Inbound ICE candidate error: ${event.errorCode} - ${event.errorText} (${event.url})`);
        };

        peerConnection.ontrack = (event) => {
            if (event.track.kind === 'audio') {
                handleRemoteAudioTrack(event);
            }
        };

        const handleConnectionStateChange = () => {
            const connState = peerConnection.connectionState;
            const iceState = peerConnection.iceConnectionState;
            console.log(`Inbound WebRTC: connection=${connState}, ice=${iceState}`);

            if (connState === 'connected' || iceState === 'connected') {
                if (!isCallConnectedRef.current) {
                    isCallConnectedRef.current = true;
                    setIsCalling(true);
                    setIsCallConnected(true);
                    startCallTimer();
                }
                // Re-assert mute in case ICE/reconnect re-enabled tracks
                applyMicMuteState(isMicMutedRef.current);
            } else if (
                connState === 'disconnected' ||
                connState === 'failed' ||
                connState === 'closed' ||
                iceState === 'disconnected' ||
                iceState === 'failed' ||
                iceState === 'closed'
            ) {
                if (iceState === 'failed') {
                    showError('ICE connection failed - check TURN server configuration')
                }
                isCallConnectedRef.current = false;
                setIsCallConnected(false);
                setIsCalling(false);
                stopCallTimer();
            }
        };

        peerConnection.onconnectionstatechange = handleConnectionStateChange;
        peerConnection.oniceconnectionstatechange = handleConnectionStateChange;

        peerConnectionRef.current = peerConnection;
        return peerConnection;
    };

    const logInboundPeerDiagnostics = (peerConnection: RTCPeerConnection, label: string, extra?: Record<string, unknown>) => {
        const audioTrack = peerConnection.getSenders().find((s) => s.track?.kind === 'audio')?.track;
        console.log(`[Inbound pickup] ${label}`, {
            iceConnectionState: peerConnection.iceConnectionState,
            connectionState: peerConnection.connectionState,
            signalingState: peerConnection.signalingState,
            audioTrack: audioTrack
                ? { enabled: audioTrack.enabled, muted: audioTrack.muted, readyState: audioTrack.readyState }
                : null,
            ...extra,
        });
    };

    const getInboundCallIds = () => resolveInboundCallIds({
        inboundCallExecutionData,
        voiceCallId,
        callingPersonDetails,
        callingWabaDetails,
        voiceCallDetails,
        getWabaNameAndPhoneNumberBytIntegratedChannelId,
    });

    const resetInboundPopupState = () => {
        clearInboundCallState();
        setVoiceCallId('');
        cleanupWebRTC();
    };

    const handleClosePopup = () => {
        setVoiceCallId('');
        clearInboundCallState();
    };

    const handleAcceptCall = async () => {
        const { callId, phoneNumberId } = getInboundCallIds();

        if (!callId) {
            showError('Call details are missing. Unable to accept the call.')
            return;
        }

        if (!localStreamRef.current) {
            const acquired = await acquireMicrophoneAccess();
            if (!acquired) return;
        }

        try {
            setIsAcceptingCall(true);
            setIsCalling(true);

            const peerConnection = await createPeerConnection();
            logInboundPeerDiagnostics(peerConnection, 'after createPeerConnection');

            const offer = await peerConnection.createOffer();
            await peerConnection.setLocalDescription(offer);
            await waitForIceGatheringComplete(peerConnection, { label: 'Inbound pickup' });
            logInboundPeerDiagnostics(peerConnection, 'after ICE gather (before POST)');

            const browserSdpOffer = peerConnection.localDescription?.sdp || '';
            if (!browserSdpOffer) {
                throw new Error('Failed to create browser SDP offer');
            }

            const payload = {
                call_id: callId,
                phone_number_id: phoneNumberId,
                flag_pickup: true,
                browser_sdp_offer: browserSdpOffer,
            };

            const res = await agent.acceptIncomingCall.post(payload);

            console.log('[Inbound pickup] handle-inbound-call response', {
                status: res?.status,
                hasServerSdpAnswer: Boolean(res?.data?.server_sdp_answer),
                serverSdpAnswerLength: res?.data?.server_sdp_answer?.length ?? 0,
                call_type: res?.data?.call_type,
            });

            if (res?.status) {
                setVoiceCallId(callId);

                if (res.data?.server_sdp_answer) {
                    await peerConnection.setRemoteDescription({
                        type: 'answer',
                        sdp: res.data.server_sdp_answer,
                    });
                    logInboundPeerDiagnostics(peerConnection, 'after setRemoteDescription(server_sdp_answer)', {
                        call_type: res.data?.call_type,
                    });
                } else {
                    throw new Error('No server SDP answer in pickup response');
                }
            } else {
                // handleError(res);
                showError(res?.error ?? 'Failed to accept the call')
                cleanupWebRTC();
                setIsCalling(false);
            }
        } catch (err: any) {
            console.error('Failed to accept inbound call:', err);
            showError(err?.message ?? 'Failed to accept the call')
            cleanupWebRTC();
            setIsCalling(false);
        } finally {
            setIsAcceptingCall(false);
        }
    };

    useEffect(() => {
        if (!flagAutoAcceptInboundCall) return;

        setFlagAutoAcceptInboundCall(false);
        void handleAcceptCall();
    }, [flagAutoAcceptInboundCall, setFlagAutoAcceptInboundCall]);

    const handleRejectCall = async () => {
        const { callId, phoneNumberId } = getInboundCallIds();

        if (!callId) {
            showError('Call details are missing. Unable to reject the call.')
            return;
        }

        try {
            setIsRejectingCall(true);

            const payload = {
                call_id: callId,
                phone_number_id: phoneNumberId,
                flag_pickup: false,
            };

            const res = await agent.acceptIncomingCall.post(payload);
            if (!res?.status) {
                // handleError(res);
                showError(res?.error ?? 'Failed to decline the call')
            }
            else {
                showSuccess(res?.error ?? 'Inbound call declined')
            }
        } catch (err: any) {
            console.error('Failed to decline inbound call:', err);
            showError(err?.message ?? 'Failed to decline the call')
        } finally {
            setIsRejectingCall(false);
            resetInboundPopupState();
        }
    };

    const handleEndCall = async () => {
        const { callId, phoneNumberId } = getInboundCallIds();
        const callerPhoneNumber = getCallerPhone() || '';

        if (!callId) {
            showError('Unable to end the call. Call details are missing.')
            return;
        }

        try {
            setIsEndingCall(true);
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));

            const payload = {
                call_id: callId,
                phone_number_id: phoneNumberId,
                phone_number: callerPhoneNumber,
            };

            const res = await agent.endIncomingCall.post(payload);
            if (res?.status) {
                // Close UI/media now, but keep voiceCallId so socket COMPLETED can toast "Call ended by …"
                clearInboundCallState();
                cleanupWebRTC();
            } else {
                // handleError(res);
                showError(res?.error ?? 'Failed to end the call')
            }
        } catch (err: any) {
            console.error('Failed to end inbound call:', err);
            showError(err?.message ?? 'Failed to end the call')
        } finally {
            setIsEndingCall(false);
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    };

    const isMicPermissionGranted = () => micPermission === 'granted';
    const isMicPermissionDenied = () => micPermission === 'denied';
    const isMicPermissionIdle = () => micPermission === 'idle';

    const getMicPermissionStatus = () => {
        if (isMicPermissionDenied()) {
            return {
                icon: '❌',
                text: 'Microphone access denied',
                textClass: `${styles.micPermissionGlobalStyle} ${styles.permissionDeniedStyles}`,
                sentence: 'Microphone access is required to accept the call. Please enable microphone access from your system settings.',
                sentenceClass: `${styles.permissionErrorText} ${styles.permissionText}`,
            };
        }
        return {
            icon: '🎤',
            text: 'Click "Allow Microphone" to enable audio',
            textClass: `${styles.micPermissionGlobalStyle} ${styles.permissionGrantStyles}`,
            sentence: 'Microphone access is required before you can accept the call.',
            sentenceClass: `${styles.permissionAccessText} ${styles.permissionText}`,
        };
    };

    const micStatus = !isMicPermissionGranted() ? getMicPermissionStatus() : null;

    useEffect(() => {
        (async () => {
            await checkInitialMicrophonePermission();
            setIsCheckingMicPermission(false);
        })();

        return () => {
            cleanupWebRTC();
        };
    }, []);

    const isPreAcceptIncoming = !isCalling && !isCallConnected;

    const getInboundCallSubtitle = () => {
        if (isCallConnected) return 'Voice call';
        if (isCalling) return 'Connecting...';
        return 'Incoming voice call';
    };

    const handleTopClose = () => {
        if (isCalling || isCallConnected) {
            if (!isEndingCall) handleEndCall();
            return;
        }
        handleClosePopup();
    };

    const renderWhatsappHeader = () => (
        <Grid item xs={12} className={`${styles.whatsappCallTopBar} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger1}`}>
            {!isCallConnected && !isCalling &&
                <button type="button" className={styles.whatsappCallCloseBtn} onClick={handleTopClose} aria-label="Close">
                    <CloseIcon />
                </button>
            }
            <div className={styles.whatsappCallBrandRow}>
                <img alt="whatsapp" src={whatsappIcon} height={20} width={20} className={globalStyles.displayFlexStyle} />
                <span className={styles.whatsappCallBrandLabel}>WhatsApp</span>
            </div>
            <div className={styles.whatsappCallTitleStyle}>{getInboundCallSubtitle()}</div>
        </Grid>
    );

    const renderTimerChip = () => (
        <div className={styles.whatsappCallTimerChip}>
            <span className={styles.whatsappCallTimerDot} aria-hidden />
            <span>{formatCallDuration(callDuration)}</span>
        </div>
    );

    const renderActiveCallControlBar = () => (
        <Grid item xs={12} className={styles.whatsappCallControlBar}>
            <div className={styles.whatsappCallControlAction}>
                <div
                    className={`${styles.inboundCallActionButtonStyle} ${isMicMuted ? styles.inboundMuteButtonActiveStyle : styles.inboundMuteButtonStyle}`}
                    onClick={toggleMicMute}
                >
                    {isMicMuted ? (
                        <MicOffIcon sx={{ fontSize: 26 }} />
                    ) : (
                        <MicIcon sx={{ fontSize: 26 }} />
                    )}
                </div>
                <span className={styles.inboundCallActionLabelStyle}>{isMicMuted ? 'Unmute' : 'Mute'}</span>
            </div>
            {isCallConnected && (
                <>
                    <div className={styles.whatsappCallControlDivider} aria-hidden />
                    <div className={styles.whatsappCallControlAction}>
                        <div
                            className={`${styles.inboundCallActionButtonStyle} ${styles.inboundRejectButtonStyle} ${isEndingCall ? styles.inboundCallActionDisabledStyle : ''}`}
                            onClick={() => !isEndingCall && handleEndCall()}
                        >
                            <CallEndIcon sx={{ color: '#fff', fontSize: 28 }} />
                        </div>
                        <span className={`${styles.inboundCallActionLabelStyle} ${styles.inboundCallEndLabelStyle}`}>End Call</span>
                    </div>
                </>
            )}
        </Grid>
    );

    const renderIncomingCallControlBar = () => (
        <Grid item xs={12} className={styles.whatsappCallControlBar}>
            <div className={styles.whatsappCallControlAction}>
                <div
                    className={`${styles.inboundCallActionButtonStyle} ${styles.inboundRejectButtonStyle} ${(isRejectingCall || isAcceptingCall) ? styles.inboundCallActionDisabledStyle : ''}`}
                    onClick={() => !isRejectingCall && !isAcceptingCall && handleRejectCall()}
                >
                    <CallEndIcon sx={{ color: '#fff', fontSize: 28 }} />
                </div>
                <span className={styles.inboundCallActionLabelStyle}>{isRejectingCall ? 'Rejecting...' : 'Decline'}</span>
            </div>
            <div className={styles.whatsappCallControlDivider} aria-hidden />
            <div className={styles.whatsappCallControlAction}>
                <div
                    className={`${styles.inboundCallActionButtonStyle} ${styles.inboundAcceptButtonStyle} ${(isAcceptingCall || isRejectingCall) ? styles.inboundCallActionDisabledStyle : ''}`}
                    onClick={() => !isAcceptingCall && !isRejectingCall && handleAcceptCall()}
                >
                    <CallIcon sx={{ color: '#fff', fontSize: 28 }} />
                </div>
                <span className={styles.inboundCallActionLabelStyle}>{isAcceptingCall ? 'Accepting...' : 'Accept'}</span>
            </div>
        </Grid>
    );

    return (
        <Grid item className={`${styles.whatsappCallSurface} ${styles.inboundCallContentEnter}`}>
            <Grid container className={styles.whatsappCallLayout}>
                {renderWhatsappHeader()}

                <Grid item xs={12} className={styles.whatsappCallBody}>
                    {!isCallConnected && !isCalling && (
                        <span className={`${styles.inboundRingingBadgeStyle} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger2}`}>Ringing...</span>
                    )}

                    {/* {isCalling && !isCallConnected && (
                        <span className={`${styles.outboundCallingBadgeStyle} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger2}`}>Connecting...</span>
                    )} */}

                    <div className={`${styles.inboundCallStaggerItem} ${styles.inboundCallStagger3}`}>
                        {isPreAcceptIncoming ? renderPulsingCallerAvatar() : (
                            <div className={styles.inboundCallerAvatarStyle}>
                                {shouldShowPersonIcon() ? (
                                    <PersonIcon sx={{ color: '#fff', fontSize: 40 }} />
                                ) : (
                                    getCallerInitial()
                                )}
                            </div>
                        )}
                    </div>

                    <div className={`${styles.inboundCallerNameStyle} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger4}`}>{getCallerName()}</div>

                    {getCallerPhone() && (
                        <div className={`${styles.inboundCallerPhoneStyle} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger4}`}>{formatPhoneNumber(getCallerPhone() || '')}</div>
                    )}

                    {isCallConnected && (
                        <div className={`${styles.inboundCallStaggerItem} ${styles.inboundCallStagger4}`}>
                            {renderTimerChip()}
                        </div>
                    )}

                    {getReceivingOnLabel() && (
                        <span className={`${styles.inboundWabaInfoStyle} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger5}`}>
                            {getReceivingOnLabel()}
                        </span>
                    )}

                    {!isCallConnected && !isCalling && !isCheckingMicPermission && !isMicPermissionGranted() && micStatus && (
                        <>
                            <Grid container className={`${micStatus.textClass} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger5}`} sx={{ width: '100%', mt: 1 }}>
                                <Grid item>{micStatus.icon}</Grid>
                                <Grid item className={styles.micAccessFontStyle}>{micStatus.text}</Grid>
                            </Grid>
                            <div className={`${micStatus.sentenceClass} ${styles.inboundCallStaggerItem} ${styles.inboundCallStagger5}`}>{micStatus.sentence}</div>
                            <GlobalButton
                                buttonText={isRequestingMic ? 'Requesting...' : isMicPermissionDenied() ? 'Retry Microphone' : 'Allow Microphone'}
                                className={isMicPermissionDenied() ? 'whatsappButtonStyle' : 'primaryButtonStyle'}
                                sx={{ minWidth: '180px', mt: 1 }}
                                disabled={isRequestingMic}
                                onClick={() => requestMicrophone()}
                            />
                        </>
                    )}
                </Grid>

                {(isCalling || isCallConnected) && renderActiveCallControlBar()}

                {!isCallConnected && !isCalling && !isCheckingMicPermission && isMicPermissionGranted() && renderIncomingCallControlBar()}
            </Grid>
        </Grid>
    );
};

export default VoiceAIInboundPopup;
